import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  Text,
  TextField,
  Select,
  Checkbox,
  Banner,
  Toast,
  BlockStack,
  InlineStack,
  DataTable,
  Spinner,
  Badge,
  Button,
  ProgressBar,
  Divider,
  Tabs,
} from "@shopify/polaris";
import {
  usePurchaseOrders,
  type POBuilderItem,
  type PurchaseOrder,
} from "../hooks/usePurchaseOrders";
import { useVendors, type Vendor } from "../hooks/useVendors";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";
import {
  formatCurrency,
  formatPercent,
} from "../utils/cost-calculations";

// ============================================================
// SALES CHART (pure CSS bar chart — no chart library needed)
// ============================================================

interface SalesMonth {
  yearMonth: string;
  label: string;
  unitsSold: number;
  revenue: number;
}

function SalesHistory({ data }: { data: SalesMonth[] }) {
  return (
    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
      {data.map((d) => (
        <div
          key={d.yearMonth}
          style={{
            textAlign: "center",
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor: d.unitsSold > 0 ? "#fef2f2" : "#f5f5f5",
            minWidth: "52px",
          }}
        >
          <div style={{ fontSize: "11px", color: "#666" }}>{d.label}</div>
          <div style={{ fontSize: "13px", fontWeight: d.unitsSold > 0 ? 600 : 400, color: d.unitsSold > 0 ? "#DA1A21" : "#999" }}>
            {d.unitsSold}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// PRODUCT CARD
// ============================================================

interface ProductCardProps {
  item: POBuilderItem;
  salesHistory: SalesMonth[];
  salesLoading: boolean;
  orderQty: string;
  manualOverride: string;
  onOrderQtyChange: (val: string) => void;
  onManualOverrideChange: (val: string) => void;
  onToggleFlag: () => void;
}

function ProductCard({
  item,
  salesHistory,
  salesLoading,
  orderQty,
  manualOverride,
  onOrderQtyChange,
  onManualOverrideChange,
  onToggleFlag,
}: ProductCardProps) {
  // Stock status
  const stockStatus =
    item.qoh === 0
      ? { tone: "critical" as const, label: "Out of Stock" }
      : item.weeksSupply < item.minWeeksSupply && item.minWeeksSupply > 0
        ? { tone: "warning" as const, label: "Low Stock" }
        : { tone: "success" as const, label: "In Stock" };

  const totalSales12mo = salesHistory.reduce((s, d) => s + d.unitsSold, 0);

  const last3mo = salesHistory.slice(-3);
  const last3moTotal = last3mo.reduce((s, d) => s + d.unitsSold, 0);

  const optionStr = item.selectedOptions
    ?.filter((o) => o.value !== "Default Title")
    .map((o) => `${o.name}: ${o.value}`)
    .join(" | ") || "";

  const boxStyle = (bg = "#f9fafb"): React.CSSProperties => ({
    backgroundColor: bg,
    borderRadius: "8px",
    padding: "10px",
    border: "1px solid #e5e7eb",
  });

  return (
    <Card>
      <BlockStack gap="300">
              {/* PRODUCT INFO: Image + Details */}
              <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: "12px" }}>
                <div style={{ width: "90px", height: "90px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e5e5e5", backgroundColor: "#f5f5f5" }}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc", fontSize: "11px" }}>No image</div>
                  )}
                </div>
                <BlockStack gap="100">
                  <InlineStack align="space-between" blockAlign="start">
                    <Text variant="headingMd" as="h2">{item.productTitle}</Text>
                    <InlineStack gap="200">
                      {item.flaggedForReview && <Badge tone="attention">Flagged</Badge>}
                      <Badge tone={stockStatus.tone}>{stockStatus.label}</Badge>
                      <Button size="slim" variant={item.flaggedForReview ? "primary" : "plain"} onClick={onToggleFlag}>
                        {item.flaggedForReview ? "Unflag" : "Flag"}
                      </Button>
                    </InlineStack>
                  </InlineStack>
                  {item.flaggedForReview && item.reviewNote && (
                    <Banner tone="warning">
                      <p>{item.reviewNote}</p>
                    </Banner>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", fontSize: "13px", color: "#555" }}>
                    {item.sku && <span><strong>SKU:</strong> {item.sku}</span>}
                    {item.vendorSku && <span><strong>Vendor Code:</strong> {item.vendorSku}</span>}
                    {item.productType && <span><strong>Type:</strong> {item.productType}</span>}
                    {item.category && item.category !== "All" && <span><strong>Category:</strong> {item.category}</span>}
                    {optionStr && <span><strong>Options:</strong> {optionStr}</span>}
                  </div>
                </BlockStack>
              </div>

              {/* Two columns: Left (Inventory + Pricing stacked) | Right (12mo Sales full height) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {/* Left column: Inventory + Pricing */}
                <BlockStack gap="200">
                  {/* Inventory Box */}
                  <div style={boxStyle()}>
                    <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">Inventory</Text>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "4px", marginTop: "6px", textAlign: "center" }}>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">QOH</Text>
                        <Text as="span" fontWeight="bold" variant="headingLg">{String(item.qoh)}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Wkly Avg</Text>
                        <Text as="span" fontWeight="bold" variant="headingLg">{item.avgWeeklyUnits > 0 ? item.avgWeeklyUnits.toFixed(1) : "0"}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Wks Supply</Text>
                        <Text as="span" fontWeight="bold" variant="headingLg">{item.weeksSupply >= 999 ? "\u221E" : item.weeksSupply > 0 ? item.weeksSupply.toFixed(1) : "0"}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Min Target</Text>
                        <Text as="span" fontWeight="bold" variant="headingLg">{item.minWeeksSupply > 0 ? item.minWeeksSupply.toFixed(0) : "-"}</Text>
                      </BlockStack>
                    </div>
                  </div>
                  {/* Pricing Box */}
                  <div style={boxStyle()}>
                    <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">Pricing</Text>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "4px", marginTop: "6px", textAlign: "center" }}>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Price</Text>
                        <Text as="span" fontWeight="bold">{formatCurrency(item.retailPrice)}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Unit Cost</Text>
                        <Text as="span" fontWeight="bold">{formatCurrency(item.unitCost)}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Pack Size</Text>
                        <Text as="span" fontWeight="bold">{String(item.packSize)}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">Margin</Text>
                        <Text as="span" fontWeight="bold">{formatPercent(item.margin)}</Text>
                      </BlockStack>
                    </div>
                  </div>
                </BlockStack>

                {/* Right column: 12 month sales - full height */}
                <div style={{ ...boxStyle(), display: "flex", flexDirection: "column" }}>
                  <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">{`Sales History (${totalSales12mo} units / 12mo)`}</Text>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "4px", marginTop: "6px", textAlign: "center", flex: 1 }}>
                    {salesLoading ? (
                      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner size="small" /></div>
                    ) : salesHistory.map((d) => (
                      <BlockStack key={d.yearMonth} gap="050">
                        <Text as="span" variant="bodySm" tone="subdued">{d.label}</Text>
                        <Text as="span" fontWeight="bold"
                          tone={d.unitsSold > 0 ? undefined : "subdued"}
                        >{String(d.unitsSold)}</Text>
                      </BlockStack>
                    ))}
                  </div>
                </div>
              </div>

              {/* ORDER ROW - green box */}
              <div style={{ ...boxStyle("#f0fdf4"), display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", alignItems: "end" }}>
                <BlockStack gap="050" inlineAlign="center">
                  <Text as="span" variant="bodySm" tone="subdued">Suggested</Text>
                  <Text as="span" fontWeight="bold" variant="headingXl">
                    {item.suggestedQty !== null ? String(item.suggestedQty) : "-"}
                  </Text>
                </BlockStack>
                <TextField
                  label="Manual Override"
                  type="number"
                  value={manualOverride}
                  onChange={onManualOverrideChange}
                  min={0}
                  autoComplete="off"
                  helpText="Blank = use suggested"
                  placeholder="-"
                />
                <BlockStack gap="050" inlineAlign="center">
                  <Text as="span" variant="bodySm" tone="subdued">On PO</Text>
                  <Text as="span" fontWeight="bold" variant="headingXl"
                    tone={parseInt(orderQty) > 0 ? "success" : "subdued"}
                  >{parseInt(orderQty) > 0 ? orderQty : "0"}</Text>
                </BlockStack>
              </div>
            </BlockStack>
    </Card>
  );
}

// ============================================================
// REVIEW SUMMARY
// ============================================================

interface ReviewProps {
  items: Array<{
    item: POBuilderItem;
    qty: number;
  }>;
  onBack: () => void;
}

function ReviewSummary({ items, onBack }: ReviewProps) {
  const totalCases = items.reduce((s, i) => s + i.qty, 0);
  const totalCost = items.reduce((s, i) => s + i.item.netCaseCost * i.qty, 0);

  const rows = items.map((i) => [
    i.item.sku || "-",
    i.item.productTitle.length > 50
      ? i.item.productTitle.substring(0, 48) + "..."
      : i.item.productTitle,
    String(i.item.qoh),
    String(i.qty),
    formatCurrency(i.item.netCaseCost),
    formatCurrency(i.item.netCaseCost * i.qty),
  ]);

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text variant="headingLg" as="h2">
            Purchase Order Summary
          </Text>
          <InlineStack gap="600">
            <BlockStack gap="100">
              <Text as="span" tone="subdued">Line Items</Text>
              <Text as="span" fontWeight="bold" variant="headingMd">
                {String(items.length)}
              </Text>
            </BlockStack>
            <BlockStack gap="100">
              <Text as="span" tone="subdued">Total Cases</Text>
              <Text as="span" fontWeight="bold" variant="headingMd">
                {String(totalCases)}
              </Text>
            </BlockStack>
            <BlockStack gap="100">
              <Text as="span" tone="subdued">Total Cost</Text>
              <Text as="span" fontWeight="bold" variant="headingMd">
                {formatCurrency(totalCost)}
              </Text>
            </BlockStack>
          </InlineStack>
        </BlockStack>
      </Card>

      <Card padding="0">
        <DataTable
          columnContentTypes={["text", "text", "numeric", "numeric", "numeric", "numeric"]}
          headings={["SKU", "Product", "QOH", "Order Qty", "Net Cost", "Line Total"]}
          rows={rows}
          hoverable
          increasedTableDensity
          totals={["", "", "", String(totalCases), "", formatCurrency(totalCost)]}
          showTotalsInFooter
        />
      </Card>

      <InlineStack align="start">
        <Button onClick={onBack}>Back to Products</Button>
      </InlineStack>
    </BlockStack>
  );
}

// ============================================================
// MAIN WIZARD
// ============================================================

export default function POBuilder() {
  const { vendorId, poId } = useParams<{ vendorId?: string; poId?: string }>();
  const navigate = useNavigate();
  const authFetch = useAuthenticatedFetch();
  const isEditing = !!poId;

  // Check for scrollTo param (from PO detail row click)
  const scrollToVariant = new URLSearchParams(window.location.search).get("scrollTo");

  const { loadBuilderData, createPurchaseOrder, updatePurchaseOrder, loadPurchaseOrder, error } =
    usePurchaseOrders();
  const { loadVendor } = useVendors();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [existingPO, setExistingPO] = useState<PurchaseOrder | null>(null);
  const [allItems, setAllItems] = useState<POBuilderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Wizard state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Per-product quantities: key = variantId, value = { order: string, override: string }
  const [quantities, setQuantities] = useState<
    Map<string, { order: string; override: string }>
  >(new Map());

  // Sales chart data
  const [salesHistory, setSalesHistory] = useState<SalesMonth[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);

  // PO fields
  const [notes, setNotes] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // Filter state
  const [filterBrand, setFilterBrand] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterOption1, setFilterOption1] = useState("");
  const [filterOption2, setFilterOption2] = useState("");
  const [filterBagSize, setFilterBagSize] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  // Compute unique filter values from allItems
  const filterOptions = useMemo(() => {
    const brands = new Set<string>();
    const types = new Set<string>();
    const tags = new Set<string>();
    const option1Values = new Set<string>();
    const option2Values = new Set<string>();
    const bagSizes = new Set<string>();

    for (const item of allItems) {
      if (item.productVendor) brands.add(item.productVendor);
      if (item.productType) types.add(item.productType);
      if (item.tags) item.tags.forEach((t) => tags.add(t));
      if (item.selectedOptions?.[0]?.value && item.selectedOptions[0].value !== "Default Title") {
        option1Values.add(item.selectedOptions[0].value);
      }
      if (item.selectedOptions?.[1]?.value) {
        option2Values.add(item.selectedOptions[1].value);
      }
      if (item.bagSize) bagSizes.add(item.bagSize);
    }

    const toSorted = (s: Set<string>) => Array.from(s).sort((a, b) => a.localeCompare(b));

    return {
      brands: toSorted(brands),
      types: toSorted(types),
      tags: toSorted(tags),
      option1Values: toSorted(option1Values),
      option2Values: toSorted(option2Values),
      bagSizes: toSorted(bagSizes),
      option1Name: allItems.find((i) => i.selectedOptions?.[0]?.name && i.selectedOptions[0].value !== "Default Title")?.selectedOptions[0].name || "Option 1",
      option2Name: allItems.find((i) => i.selectedOptions?.[1])?.selectedOptions[1].name || "Option 2",
    };
  }, [allItems]);

  const hasActiveFilters = filterBrand || filterType || filterTag || filterOption1 || filterOption2 || filterBagSize || showFlaggedOnly;

  // Filtered items based on toggle + search + filters
  const displayItems = useMemo(() => {
    let items = allItems;
    if (!showAllProducts) {
      items = items.filter(
        (item) =>
          item.suggestedQty !== null ||
          item.qoh === 0 ||
          parseInt(quantities.get(item.shopifyVariantId)?.order || "0") > 0
      );
    }
    if (filterBrand) {
      items = items.filter((item) => item.productVendor === filterBrand);
    }
    if (filterType) {
      items = items.filter((item) => item.productType === filterType);
    }
    if (filterTag) {
      items = items.filter((item) => item.tags?.includes(filterTag));
    }
    if (filterOption1) {
      items = items.filter((item) => item.selectedOptions?.[0]?.value === filterOption1);
    }
    if (filterOption2) {
      items = items.filter((item) => item.selectedOptions?.[1]?.value === filterOption2);
    }
    if (filterBagSize) {
      items = items.filter((item) => item.bagSize === filterBagSize);
    }
    if (showFlaggedOnly) {
      items = items.filter((item) => item.flaggedForReview);
    }
    if (searchValue) {
      const s = searchValue.toLowerCase();
      items = items.filter(
        (item) =>
          item.productTitle.toLowerCase().includes(s) ||
          (item.sku && item.sku.toLowerCase().includes(s)) ||
          (item.vendorSku && item.vendorSku.toLowerCase().includes(s)) ||
          (item.variantTitle && item.variantTitle.toLowerCase().includes(s))
      );
    }
    return items;
  }, [allItems, showAllProducts, quantities, searchValue, filterBrand, filterType, filterTag, filterOption1, filterOption2, filterBagSize, showFlaggedOnly]);

  const currentItem = displayItems[currentIndex] || null;

  // Load builder data
  useEffect(() => {
    async function init() {
      setLoading(true);

      const targetVendorId = vendorId || (isEditing && poId ? undefined : undefined);
      let vid = vendorId;

      let loadedPO: PurchaseOrder | null = null;
      if (isEditing && poId) {
        loadedPO = await loadPurchaseOrder(poId);
        if (!loadedPO) { setLoading(false); return; }
        setExistingPO(loadedPO);
        setNotes(loadedPO.notes || "");
        setExpectedDelivery(loadedPO.expectedDelivery || "");
        vid = loadedPO.vendorId;
      }

      if (vid) {
        const v = await loadVendor(vid);
        setVendor(v);

        const items = await loadBuilderData(vid);
        setAllItems(items);

        // Initialize quantities
        const qtyMap = new Map<string, { order: string; override: string }>();

        if (isEditing && loadedPO?.lineItems) {
          // When editing, restore quantities from the existing PO
          for (const item of items) {
            const existingLine = loadedPO.lineItems.find(
              (li) => li.shopifyVariantId === item.shopifyVariantId
            );
            qtyMap.set(item.shopifyVariantId, {
              order: existingLine ? String(existingLine.quantityOrdered) : "",
              override: "",
            });
          }
        } else {
          // New PO: start with empty quantities — user adds via filters + "Add All Filtered"
          for (const item of items) {
            qtyMap.set(item.shopifyVariantId, {
              order: "",
              override: "",
            });
          }
        }
        setQuantities(qtyMap);

        // Default expected delivery
        if (v?.leadTimeDays && !isEditing) {
          const d = new Date();
          d.setDate(d.getDate() + v.leadTimeDays);
          setExpectedDelivery(d.toISOString().split("T")[0]);
        }
      }

      setLoading(false);
    }
    init();
  }, [vendorId, poId]);

  // Jump to specific product if scrollTo param is set (from PO Detail row click)
  useEffect(() => {
    if (scrollToVariant && displayItems.length > 0) {
      const idx = displayItems.findIndex(
        (item) => item.shopifyVariantId === scrollToVariant
      );
      if (idx >= 0) {
        setCurrentIndex(idx);
      } else {
        // Might be in "show all" — enable it and try again
        setShowAllProducts(true);
      }
    }
  }, [scrollToVariant, displayItems.length]);

  // Load sales history when current product changes
  useEffect(() => {
    if (!currentItem) return;
    setSalesLoading(true);
    authFetch(`/api/velocity/history/${currentItem.shopifyVariantId}`)
      .then((res) => res.json())
      .then((json) => setSalesHistory(json.data || []))
      .catch(() => setSalesHistory([]))
      .finally(() => setSalesLoading(false));
  }, [currentItem?.shopifyVariantId]);

  // Quantity handlers
  const handleManualOverrideChange = useCallback(
    (value: string) => {
      if (!currentItem) return;
      setQuantities((prev) => {
        const next = new Map(prev);
        const existing = next.get(currentItem.shopifyVariantId) || { order: "", override: "" };
        // If manual override is set, it becomes the order qty
        const orderQty = value
          ? value
          : currentItem.suggestedQty
            ? String(currentItem.suggestedQty)
            : "";
        next.set(currentItem.shopifyVariantId, {
          order: orderQty,
          override: value,
        });
        return next;
      });
    },
    [currentItem]
  );

  const handleOrderQtyChange = useCallback(
    (value: string) => {
      if (!currentItem) return;
      setQuantities((prev) => {
        const next = new Map(prev);
        const existing = next.get(currentItem.shopifyVariantId) || { order: "", override: "" };
        next.set(currentItem.shopifyVariantId, { ...existing, order: value });
        return next;
      });
    },
    [currentItem]
  );

  // Toggle flag for review on current product
  const handleToggleFlag = useCallback(async () => {
    if (!currentItem?.vendorProductId) return;
    const vid = vendor?.id || vendorId || "";
    try {
      const res = await authFetch(
        `/api/vendors/${vid}/products/${currentItem.vendorProductId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ flaggedForReview: !currentItem.flaggedForReview }),
        }
      );
      if (res.ok) {
        setAllItems((prev) =>
          prev.map((item) =>
            item.shopifyVariantId === currentItem.shopifyVariantId
              ? { ...item, flaggedForReview: !item.flaggedForReview }
              : item
          )
        );
        setToastMessage(currentItem.flaggedForReview ? "Flag removed" : "Flagged for review");
      }
    } catch {
      setToastMessage("Failed to update flag");
    }
  }, [currentItem, vendor, vendorId, authFetch]);

  // Add all currently filtered items to PO (only those with suggested qty or override)
  const handleAddAllFiltered = useCallback(() => {
    setQuantities((prev) => {
      const next = new Map(prev);
      for (const item of displayItems) {
        const existing = next.get(item.shopifyVariantId) || { order: "", override: "" };
        // Only set if it has a suggested qty and isn't already on PO
        if (item.suggestedQty && item.suggestedQty > 0 && !parseInt(existing.override || "0")) {
          next.set(item.shopifyVariantId, {
            ...existing,
            order: String(item.suggestedQty),
          });
        }
      }
      return next;
    });
    setToastMessage(`Added ${displayItems.filter((i) => i.suggestedQty && i.suggestedQty > 0).length} items to PO`);
  }, [displayItems]);

  const clearAllFilters = useCallback(() => {
    setFilterBrand("");
    setFilterType("");
    setFilterTag("");
    setFilterOption1("");
    setFilterOption2("");
    setFilterBagSize("");
    setShowFlaggedOnly(false);
    setSearchValue("");
    setCurrentIndex(0);
  }, []);

  // Navigation
  const goNext = useCallback(() => {
    if (currentIndex < displayItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, displayItems.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  // Items that will go on the PO
  const orderItems = useMemo(() => {
    return allItems
      .filter((item) => {
        const q = quantities.get(item.shopifyVariantId);
        return q && parseInt(q.order) > 0;
      })
      .map((item) => ({
        item,
        qty: parseInt(quantities.get(item.shopifyVariantId)!.order),
      }));
  }, [allItems, quantities]);

  // Save PO
  const handleSave = useCallback(
    async (status: "draft" | "submitted") => {
      if (orderItems.length === 0) {
        setToastMessage("No items to order.");
        return;
      }
      setSaving(true);

      const lineItems = orderItems.map((oi, index) => ({
        vendorProductId: oi.item.vendorProductId,
        shopifyVariantId: oi.item.shopifyVariantId,
        shopifyProductId: oi.item.shopifyProductId,
        shopifyInventoryItemId: oi.item.shopifyInventoryItemId,
        sku: oi.item.sku,
        productTitle: oi.item.productTitle,
        variantTitle: oi.item.variantTitle,
        packSize: oi.item.packSize,
        caseCost: oi.item.caseCost,
        offInvoice: oi.item.offInvoice,
        byCheque: oi.item.byCheque,
        freeGoods: oi.item.freeGoods,
        vendorDiscountPct: oi.item.vendorDiscountPct,
        retailPrice: oi.item.retailPrice,
        quantityOrdered: oi.qty,
        sortOrder: index,
      }));

      const payload = {
        vendorId: vendor?.id || vendorId || "",
        status,
        notes: notes || undefined,
        expectedDelivery: expectedDelivery || undefined,
        lineItems,
      };

      let result: PurchaseOrder | null;
      if (isEditing && poId) {
        result = await updatePurchaseOrder(poId, payload);
      } else {
        result = await createPurchaseOrder(payload);
      }

      setSaving(false);
      if (result) {
        setToastMessage(
          status === "submitted"
            ? `PO ${result.poNumber} submitted`
            : `PO ${result.poNumber} saved as draft`
        );
        navigate(`/purchase-orders/${result.id}`);
      }
    },
    [orderItems, vendor, vendorId, notes, expectedDelivery, isEditing, poId, navigate]
  );

  if (loading) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const currentQty = currentItem
    ? quantities.get(currentItem.shopifyVariantId) || { order: "", override: "" }
    : { order: "", override: "" };

  const progress =
    displayItems.length > 0
      ? ((currentIndex + 1) / displayItems.length) * 100
      : 0;

  return (
    <>
      <Page
        title={
          showReview
            ? "Review Purchase Order"
            : isEditing
              ? `Edit ${existingPO?.poNumber || "Draft"}`
              : `New PO — ${vendor?.name || ""}`
        }
        backAction={{
          content: "Back",
          url: isEditing ? `/purchase-orders/${poId}` : `/vendors/${vendorId}`,
        }}
        primaryAction={
          showReview
            ? {
                content: "Submit PO",
                onAction: () => handleSave("submitted"),
                loading: saving,
              }
            : currentIndex === displayItems.length - 1
              ? {
                  content: `Review PO (${orderItems.length} items)`,
                  onAction: () => setShowReview(true),
                  disabled: orderItems.length === 0,
                }
              : undefined
        }
        secondaryActions={
          showReview
            ? [
                {
                  content: "Save as Draft",
                  onAction: () => handleSave("draft"),
                  loading: saving,
                },
              ]
            : [
                ...(orderItems.length > 0
                  ? [
                      {
                        content: `Review PO (${orderItems.length} items)`,
                        onAction: () => setShowReview(true),
                      },
                    ]
                  : []),
                {
                  content: "Save Draft",
                  onAction: () => handleSave("draft"),
                  loading: saving,
                  disabled: orderItems.length === 0,
                },
              ]
        }
      >
        {error && (
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout>
          {showReview ? (
            <Layout.Section>
              <ReviewSummary
                items={orderItems}
                onBack={() => setShowReview(false)}
              />
            </Layout.Section>
          ) : (
            <>
              {/* Top bar: counter + PO fields */}
              <Layout.Section>
                <div style={{ border: "1px solid #d1d5db", borderRadius: "10px", backgroundColor: "#fff", padding: "12px 16px" }}>
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" variant="bodySm" fontWeight="semibold">
                          {`Product ${displayItems.length > 0 ? currentIndex + 1 : 0} of ${displayItems.length}`}
                        </Text>
                        {orderItems.length > 0 && (
                          <Badge tone="info">{`${orderItems.length} on PO`}</Badge>
                        )}
                        <Checkbox
                          label="Show all"
                          checked={showAllProducts}
                          onChange={(v) => { setShowAllProducts(v); setCurrentIndex(0); }}
                        />
                        <Checkbox
                          label="Flagged only"
                          checked={showFlaggedOnly}
                          onChange={(v) => { setShowFlaggedOnly(v); setCurrentIndex(0); }}
                        />
                      </InlineStack>
                      <InlineStack gap="200" blockAlign="center">
                        <div style={{ width: "130px" }}>
                          <TextField label="" labelHidden type="date" value={expectedDelivery} onChange={setExpectedDelivery} autoComplete="off" size="slim" />
                        </div>
                        <div style={{ width: "130px" }}>
                          <TextField label="" labelHidden value={notes} onChange={setNotes} autoComplete="off" placeholder="PO notes..." size="slim" />
                        </div>
                      </InlineStack>
                    </InlineStack>

                    {/* Search */}
                    <TextField
                      label=""
                      labelHidden
                      value={searchValue}
                      onChange={(v) => { setSearchValue(v); setCurrentIndex(0); }}
                      placeholder="Search by product name, SKU, or vendor code..."
                      clearButton
                      onClearButtonClick={() => { setSearchValue(""); setCurrentIndex(0); }}
                      autoComplete="off"
                      size="slim"
                    />

                    {/* Filter chips row */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                      {filterOptions.brands.length > 1 && (
                        <div style={{ minWidth: "120px" }}>
                          <Select
                            label=""
                            labelHidden
                            options={[{ label: "All Brands", value: "" }, ...filterOptions.brands.map((b) => ({ label: b, value: b }))]}
                            value={filterBrand}
                            onChange={(v) => { setFilterBrand(v); setCurrentIndex(0); }}
                          />
                        </div>
                      )}
                      {filterOptions.types.length > 1 && (
                        <div style={{ minWidth: "120px" }}>
                          <Select
                            label=""
                            labelHidden
                            options={[{ label: "All Types", value: "" }, ...filterOptions.types.map((t) => ({ label: t, value: t }))]}
                            value={filterType}
                            onChange={(v) => { setFilterType(v); setCurrentIndex(0); }}
                          />
                        </div>
                      )}
                      {filterOptions.tags.length > 0 && (
                        <div style={{ minWidth: "120px" }}>
                          <Select
                            label=""
                            labelHidden
                            options={[{ label: "All Tags", value: "" }, ...filterOptions.tags.map((t) => ({ label: t, value: t }))]}
                            value={filterTag}
                            onChange={(v) => { setFilterTag(v); setCurrentIndex(0); }}
                          />
                        </div>
                      )}
                      {filterOptions.option1Values.length > 1 && (
                        <div style={{ minWidth: "120px" }}>
                          <Select
                            label=""
                            labelHidden
                            options={[{ label: `All ${filterOptions.option1Name}`, value: "" }, ...filterOptions.option1Values.map((v) => ({ label: v, value: v }))]}
                            value={filterOption1}
                            onChange={(v) => { setFilterOption1(v); setCurrentIndex(0); }}
                          />
                        </div>
                      )}
                      {filterOptions.option2Values.length > 1 && (
                        <div style={{ minWidth: "120px" }}>
                          <Select
                            label=""
                            labelHidden
                            options={[{ label: `All ${filterOptions.option2Name}`, value: "" }, ...filterOptions.option2Values.map((v) => ({ label: v, value: v }))]}
                            value={filterOption2}
                            onChange={(v) => { setFilterOption2(v); setCurrentIndex(0); }}
                          />
                        </div>
                      )}
                      {filterOptions.bagSizes.length > 1 && (
                        <div style={{ minWidth: "100px" }}>
                          <Select
                            label=""
                            labelHidden
                            options={[{ label: "All Sizes", value: "" }, ...filterOptions.bagSizes.map((s) => ({ label: s, value: s }))]}
                            value={filterBagSize}
                            onChange={(v) => { setFilterBagSize(v); setCurrentIndex(0); }}
                          />
                        </div>
                      )}
                      {hasActiveFilters && (
                        <Button size="slim" onClick={clearAllFilters} variant="plain">Clear filters</Button>
                      )}
                    </div>

                    {/* Add All Filtered + info */}
                    {displayItems.length > 0 && (
                      <InlineStack gap="200" blockAlign="center">
                        <Button
                          size="slim"
                          onClick={handleAddAllFiltered}
                        >
                          {`Add all ${displayItems.filter((i) => i.suggestedQty && i.suggestedQty > 0).length} with suggested qty to PO`}
                        </Button>
                        {hasActiveFilters && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {`Showing ${displayItems.length} of ${allItems.length} products`}
                          </Text>
                        )}
                      </InlineStack>
                    )}
                  </BlockStack>
                </div>
              </Layout.Section>

              {/* Product Card */}
              {currentItem ? (
                <Layout.Section>
                  <ProductCard
                    item={currentItem}
                    salesHistory={salesHistory}
                    salesLoading={salesLoading}
                    orderQty={currentQty.order}
                    manualOverride={currentQty.override}
                    onOrderQtyChange={handleOrderQtyChange}
                    onManualOverrideChange={handleManualOverrideChange}
                    onToggleFlag={handleToggleFlag}
                  />
                </Layout.Section>
              ) : (
                <Layout.Section>
                  <Card>
                    <Text as="p" tone="subdued">
                      No products to display. Try toggling "Show all products".
                    </Text>
                  </Card>
                </Layout.Section>
              )}

              {/* Navigation buttons at BOTTOM */}
              <Layout.Section>
                <InlineStack align="space-between">
                  <Button onClick={goPrev} disabled={currentIndex === 0}>
                    {"← Previous"}
                  </Button>
                  <Button
                    onClick={goNext}
                    disabled={currentIndex >= displayItems.length - 1}
                    variant="primary"
                  >
                    {"Next →"}
                  </Button>
                </InlineStack>
              </Layout.Section>
            </>
          )}
        </Layout>

        {toastMessage && (
          <Toast content={toastMessage} onDismiss={() => setToastMessage(null)} />
        )}
      </Page>
    </>
  );
}
